const MIN_PERCEPTIBLE_PX = 0.5;

export const DEFAULT_INVERSION_TOLERANCE = 0.05;

export type OrderItem = {
  key: string;
  group: string;
  raw: number;
  rendered: number;
};

const isWorseThan = (
  rendered: number,
  reference: number,
  tolerance: number,
  minDelta: number
): boolean =>
  rendered > reference * (1 + tolerance) && rendered - reference > minDelta;

const groupByRaw = (items: OrderItem[]): OrderItem[][] => {
  const blocks: OrderItem[][] = [];
  for (const item of [...items].sort((a, b) => b.raw - a.raw)) {
    const last = blocks[blocks.length - 1];
    if (last && last[0]?.raw === item.raw) last.push(item);
    else blocks.push([item]);
  }
  return blocks;
};

export const findOrderInversions = (
  items: OrderItem[],
  tolerance: number,
  minDelta = MIN_PERCEPTIBLE_PX
): Set<string> => {
  const byGroup = new Map<string, OrderItem[]>();
  for (const item of items) {
    const group = byGroup.get(item.group);
    if (group) group.push(item);
    else byGroup.set(item.group, [item]);
  }

  const inverted = new Set<string>();
  for (const group of byGroup.values()) {
    const blocks = groupByRaw(group);

    let minAbove = Number.POSITIVE_INFINITY;
    for (const block of blocks) {
      for (const item of block) {
        if (isWorseThan(item.rendered, minAbove, tolerance, minDelta)) {
          inverted.add(item.key);
        }
      }
      for (const item of block) {
        minAbove = Math.min(minAbove, item.rendered);
      }
    }

    let maxBelow = Number.NEGATIVE_INFINITY;
    for (const block of [...blocks].reverse()) {
      for (const item of block) {
        if (isWorseThan(maxBelow, item.rendered, tolerance, minDelta)) {
          inverted.add(item.key);
        }
      }
      for (const item of block) {
        maxBelow = Math.max(maxBelow, item.rendered);
      }
    }
  }
  return inverted;
};
